import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Cpu, ChefHat, Plane, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Link } from "react-router-dom";

export default function PlanBuilder() {
  const [stepIdx, setStepIdx] = useState(0);
  const [rooms, setRooms] = useState(2);
  const [devices, setDevices] = useState(6);
  const [kitchen, setKitchen] = useState(false);
  const [travel, setTravel] = useState(false);

  const result = useMemo(() => {
    // Service plan by devices
    let plan = "Revo Basic", serviceMonthly = 6.49, serviceAnnual = 69;
    if (devices > 10) { plan = "Revo Home Max"; serviceMonthly = 20.99; serviceAnnual = 225; }
    else if (devices > 3) { plan = "Revo Home"; serviceMonthly = 12.95; serviceAnnual = 139; }

    // Install package by rooms
    let install = "Starter Kit", installTotal = 229, installLabel = "1 Revo Surface + Revo Base receiver";
    if (rooms >= 4) { install = "Home Max Install"; installTotal = 899; installLabel = "5 Surfaces + 2 Ceiling Units + Revo Go"; }
    else if (rooms >= 2) { install = "Home Install"; installTotal = 599; installLabel = "3 Surfaces + 1 Ceiling Unit"; }

    let serviceAddon = 0;
    if (kitchen) { installTotal += 798; serviceAddon += 9.99; }
    if (travel && install !== "Home Max Install") { installTotal += 299; }

    const monthly = +(serviceMonthly + serviceAddon).toFixed(2);
    const annualPrice = +(serviceAnnual + serviceAddon * 10).toFixed(0);
    const financed = +(installTotal / 12).toFixed(2);
    return { plan, monthly, annualPrice, install, installLabel, installTotal, financed, serviceAddon };
  }, [rooms, devices, kitchen, travel]);

  const steps = [
    {
      title: "How many rooms?",
      body: (
        <Counter value={rooms} setValue={setRooms} min={1} max={8} testid="rooms" icon={Home} unit="rooms" />
      ),
    },
    {
      title: "How many devices?",
      body: (
        <Counter value={devices} setValue={setDevices} min={1} max={20} testid="devices" icon={Cpu} unit="devices" />
      ),
    },
    {
      title: "Kitchen? Travel?",
      body: (
        <div className="flex flex-col gap-4">
          <Toggle active={kitchen} onClick={() => setKitchen((v) => !v)} icon={ChefHat} label="Add Revo Kitchen (Ki)" sub="Cordless, battery-free appliances" testid="kitchen" />
          <Toggle active={travel} onClick={() => setTravel((v) => !v)} icon={Plane} label="Add Revo Go" sub="A flight-legal Surface for the road" testid="travel" />
        </div>
      ),
    },
  ];

  const isLast = stepIdx === steps.length;

  return (
    <div className="overflow-hidden rounded-3xl border border-white/10 bg-navy-deep" data-testid="poc-plan-builder">
      <div className="grid lg:grid-cols-[1fr_1fr]">
        {/* Wizard */}
        <div className="border-b border-white/10 p-7 md:p-10 lg:border-b-0 lg:border-r">
          <div className="mb-8 flex items-center justify-between">
            <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-revo-green">Plan builder</p>
            <span className="text-sm text-revo-ice/60">{Math.min(stepIdx + 1, steps.length)} / {steps.length}</span>
          </div>

          <AnimatePresence mode="wait">
            {!isLast ? (
              <motion.div key={stepIdx} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}>
                <h3 className="font-display text-3xl font-extrabold text-white">{steps[stepIdx].title}</h3>
                <div className="mt-8">{steps[stepIdx].body}</div>
              </motion.div>
            ) : (
              <motion.div key="done" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h3 className="font-display text-3xl font-extrabold text-white">Your ChargeRevo setup</h3>
                <p className="mt-2 text-sm text-revo-ice">Built from {rooms} rooms · {devices} devices{kitchen ? " · Kitchen" : ""}{travel ? " · Go" : ""}.</p>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-10 flex gap-3">
            {stepIdx > 0 && (
              <button onClick={() => setStepIdx((s) => s - 1)} data-testid="plan-back-btn" className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-revo-ice transition-colors hover:text-white">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            )}
            {!isLast ? (
              <button onClick={() => setStepIdx((s) => s + 1)} data-testid="plan-next-btn" className="flex items-center gap-2 rounded-full bg-revo-green px-6 py-3 text-sm font-bold text-navy transition-transform duration-200 hover:scale-[1.03]">
                {stepIdx === steps.length - 1 ? "See my plan" : "Next"} <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={() => setStepIdx(0)} data-testid="plan-restart-btn" className="rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-revo-ice transition-colors hover:text-white">
                Start over
              </button>
            )}
          </div>
        </div>

        {/* Live result */}
        <div className="relative p-7 md:p-10">
          <div className="mb-5">
            <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-revo-ice/70">Your setup</p>
          </div>

          <div className="rounded-2xl border border-revo-green/30 bg-revo-green/5 p-6">
            <span className="inline-block rounded-full bg-revo-green px-3 py-1 text-xs font-bold text-navy">{result.plan}</span>
            <p className="mt-4 font-display text-3xl font-black text-white" data-testid="plan-service-price">Pricing revealed at launch</p>
            <p className="mt-1 text-sm text-revo-ice">Monthly service{result.serviceAddon > 0 ? " · includes Kitchen service" : ""}</p>
          </div>

          <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <p className="text-sm text-revo-ice">One-time install · {result.install}</p>
            <p className="text-xs text-revo-ice/60">{result.installLabel}</p>
            <p className="mt-3 font-display text-2xl font-extrabold text-white" data-testid="plan-install-price">Pricing revealed at launch</p>
            <p className="mt-1 text-xs text-revo-green">0% financing available at checkout</p>
          </div>

          <div className="mt-4 flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-xs text-revo-ice" data-testid="plan-cords-footnote">
            <Check className="h-4 w-4 text-revo-green" />
            Replaces the drawer of cables, adapters &amp; battery wear you keep re-buying
          </div>

          <div className="mt-4 rounded-xl bg-revo-green/10 px-4 py-3 text-xs font-semibold text-revo-green" data-testid="plan-founding-banner">
            First 1,000 homes: professional installation free — plus first hardware allocation.
          </div>

          <Link to="/join" data-testid="plan-reserve-btn" className="mt-5 block rounded-full bg-revo-green py-3 text-center text-sm font-bold text-navy transition-transform duration-200 hover:scale-[1.02]">
            Reserve my spot
          </Link>
        </div>
      </div>
    </div>
  );
}

function Counter({ value, setValue, min, max, testid, icon: Icon, unit }) {
  return (
    <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] p-6">
      <button onClick={() => setValue(Math.max(min, value - 1))} data-testid={`${testid}-minus`} className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 text-2xl text-white transition-colors hover:border-revo-green hover:text-revo-green">−</button>
      <div className="flex flex-col items-center">
        <Icon className="mb-2 h-6 w-6 text-revo-green" />
        <span className="font-display text-5xl font-black text-white" data-testid={`${testid}-value`}>{value}</span>
        <span className="text-xs uppercase tracking-wider text-revo-ice/60">{unit}</span>
      </div>
      <button onClick={() => setValue(Math.min(max, value + 1))} data-testid={`${testid}-plus`} className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 text-2xl text-white transition-colors hover:border-revo-green hover:text-revo-green">+</button>
    </div>
  );
}

function Toggle({ active, onClick, icon: Icon, label, sub, testid }) {
  return (
    <button onClick={onClick} data-testid={`toggle-${testid}`} className={`flex items-center gap-4 rounded-2xl border p-5 text-left transition-colors duration-200 ${active ? "border-revo-green bg-revo-green/10" : "border-white/10 bg-white/[0.03] hover:border-white/25"}`}>
      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${active ? "bg-revo-green text-navy" : "bg-white/5 text-revo-ice"}`}><Icon className="h-5 w-5" /></span>
      <div className="flex-1">
        <p className="font-display font-bold text-white">{label}</p>
        <p className="text-xs text-revo-ice/60">{sub}</p>
      </div>
      <span className={`flex h-6 w-6 items-center justify-center rounded-full border ${active ? "border-revo-green bg-revo-green text-navy" : "border-white/25"}`}>{active && <Check className="h-4 w-4" />}</span>
    </button>
  );
}
